import { Flex, SimpleGrid, Skeleton } from '@mantine/core';
import useTranslation from 'next-translate/useTranslation';
import { useRouter } from 'next/router';
import { useCallback } from 'react';

import { useIsMobile, useIsNotebook, useIsTablet } from '~/domains/common/hooks/mediaQueries';
import ConnectedContestCard from '~/domains/contest/domains/common/components/ContestCard/ConnectedContestCard';
import { ContestStatus } from '~/domains/contest/domains/services/enums/contests-status.enum';
import { UserContestAssociation } from '~/domains/contest/domains/services/enums/user-contest-association.enum';
import { Text } from '~/domains/design-system/Text';
import { NoActiveContests } from '~/domains/commissioner/components/OnboardingWidget/types/NoActiveContests';
import { NoFinalContests } from '~/domains/commissioner/components/OnboardingWidget/types/NoFinalContests';
import { NoContests } from '~/domains/commissioner/components/OnboardingWidget/types/NoContests';
import { NoFilteredContests } from '~/domains/commissioner/components/OnboardingWidget/types/NoFilteredContests';
import dt from '~/testing';
import { Routes } from '~/domains/common/constants/routes';
import { URLQueryKeys } from '~/domains/common/types/URLQueryKeys';
import { ActiveTab } from '~/domains/commissioner/enums';

import useChannelSummary from './useChannelSummary';

export function ChannelContests({
  children,
  isFullWidth,
}: {
  children: React.ReactNode;
  isFullWidth?: boolean;
}) {
  const isMobile = useIsMobile();
  const isNotebook = useIsNotebook();

  // @TODO: This will be used in the future designs most probably
  let columnsCount = isFullWidth ? 3 : 2;
  if (isNotebook) {
    columnsCount = isFullWidth ? 2 : 1;
  }
  if (isMobile) {
    columnsCount = 1;
  }

  return (
    <SimpleGrid cols={columnsCount} /* eslint-disable-line local-rules/use-theme-tokens */ spacing={14} verticalSpacing={isMobile ? 8 : 16}>
      {children}
    </SimpleGrid>
  );
}

type ChannelContestsEmptyStateProps = {
  association?: UserContestAssociation;
  isFinalView?: boolean;
  isCurrentUserChannel?: boolean;
  handle?: string;
  userId?: string;
};

function ChannelContestsEmptyState({
  association,
  isFinalView,
  isCurrentUserChannel,
  handle,
  userId,
}: ChannelContestsEmptyStateProps) {
  const { t } = useTranslation('channel');
  const router = useRouter();
  const { query } = router;
  const { totalContests } = useChannelSummary(userId);

  const handleFinalContestsClick = useCallback(() => {
    router.push(Routes.commissioner({ [URLQueryKeys.ACTIVE_TAB]: ActiveTab.FINAL }));
  }, [handle, router]);

  const handleCacClick = useCallback(() => {
    router.push(Routes.createAContest());
  }, [router]);

  // Logic for determining if there are any filters applied
  if (
    query[URLQueryKeys.CONTEST_STATUS] ||
    query[URLQueryKeys.CONTEST_TYPE] ||
    query[URLQueryKeys.LEAGUES] ||
    query[URLQueryKeys.RESTART_STATUS]
  ) {
    return <NoFilteredContests />;
  }

  // Only show new onboarding widget for "created" contests and not public channels
  if (isCurrentUserChannel && association !== UserContestAssociation.ENTERED) {
    if (totalContests === 0) {
      return <NoContests onCacClick={handleCacClick} />;
    }

    if (isFinalView) {
      return <NoFinalContests />;
    }

    return (
      <NoActiveContests
        onCacClick={handleCacClick}
        onFinalContestsClick={handleFinalContestsClick}
      />
    );
  }

  return (
    <Flex direction="column" /* eslint-disable-line local-rules/use-theme-tokens */ gap={8} align="center" mih={220} justify="center" sx={{ flex: 1 }}>
      <Text
        data-test-id={dt.channel.channelContests.channelContestsEmptyState}
        variant="headline-xxl"
        color="gray2.3"
      >
        {association === UserContestAssociation.ENTERED
          ? t('contests.noContests.entered.title')
          : t('contests.noContests.created.title')}
      </Text>
      <Text variant="body-large" color="gray2.3">
        {association === UserContestAssociation.ENTERED
          ? t('contests.noContests.entered.other', { handle })
          : t('contests.noContests.created.other', { handle })}
      </Text>
    </Flex>
  );
}

function ChannelContestsSkeleton() {
  return <Skeleton h={218} radius="lg" />;
}

ChannelContests.EmptyState = ChannelContestsEmptyState;

ChannelContests.ContestCard = ConnectedContestCard;

ChannelContests.Skeleton = ChannelContestsSkeleton;
